import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import DataSourceIndicator from './DataSourceIndicator';
import { coerceWidgetDecimals } from '../../../utils/signalKValueFormat';
import { INSTRUMENT, instrumentBarColor, isWasteOrHoldingTank } from './instrumentLook';

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: relative;
`;

const WidgetTitle = styled.h3`
  margin: 0;
  color: ${INSTRUMENT.label};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  flex: 1;
  min-width: 0;
`;

const WidgetTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.5rem;
`;

const WidgetValue = styled.div`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${INSTRUMENT.value};
  margin-bottom: 0.25rem;
`;

const WidgetUnit = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${INSTRUMENT.muted};
  font-weight: ${theme.fontWeights.medium};
`;

// Helper function to get color based on configured color ranges
const getColorForValue = (value, colorRanges) => {
  if (!colorRanges || !Array.isArray(colorRanges) || colorRanges.length === 0) {
    // Default tank colors (good when high - freshwater style)
    if (value > 75) return '#10b981'; // green
    if (value > 50) return '#eab308'; // yellow
    if (value > 25) return '#f59e0b'; // orange
    return '#ef4444'; // red
  }
  
  // Find matching color range
  for (const range of colorRanges) {
    const min = parseFloat(range.min) || 0;
    const max = parseFloat(range.max) || 100;
    
    if (value >= min && value <= max) {
      return range.color || '#6b7280';
    }
  }
  
  // Default gray if no range matches
  return '#6b7280';
};

// Classic Circular Gauge (like FlowFuse)
export const CircularGauge = ({ name, value, unit, min = 0, max = 100, config = {}, sourceType, isLive = false, lastUpdate = null }) => {
  // Handle zero values properly - don't treat 0 as falsy
  const parsedValue = parseFloat(value);
  const rawValue = isNaN(parsedValue) ? 0 : parsedValue;
  
  // Track if we converted from ratio to percentage
  let numValue = rawValue;
  let wasConvertedFromRatio = false;
  
  // If unit is %, multiply value by 100 (for ratio values like stateOfCharge)
  if (unit === '%' && numValue >= 0 && numValue <= 1) {
    numValue = numValue * 100;
    wasConvertedFromRatio = true;
  }
  
  const decimals = coerceWidgetDecimals(config, 1);
  const range = Math.max(max - min, 0.001); // Avoid division by zero
  const percentage = ((numValue - min) / range) * 100;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  
  // Gauge configuration
  const size = config.size || 120;
  const strokeWidth = config.strokeWidth || 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (clampedPercentage / 100) * circumference;
  
  // Color determination - use the DISPLAYED value for color range checking
  // This ensures color ranges match what the user sees on screen
  const getColor = (displayValue) => {
    // Check colorRanges first (value-based ranges from widget config)
    // Use the displayed value so color ranges match what user sees
    if (config.colorRanges && Array.isArray(config.colorRanges)) {
      for (const range of config.colorRanges) {
        if (displayValue >= range.min && displayValue <= range.max) {
          return range.color;
        }
      }
    }
    
    // Fallback to percentage-based colors if no colorRanges
    if (percentage < 25) return '#ef4444'; // red
    if (percentage < 50) return '#f59e0b'; // orange
    if (percentage < 75) return '#eab308'; // yellow
    return '#10b981'; // green
  };

  const gaugeColor = getColor(numValue);

  return (
    <WidgetContainer>
      <WidgetTitleRow>
        <WidgetTitle>{name}</WidgetTitle>
        <DataSourceIndicator sourceType={sourceType || (config?.forceDemo || config?.dataMode === 'demo' ? 'demo' : (isLive ? 'live' : 'database'))} lastUpdate={lastUpdate} />
      </WidgetTitleRow>
      <div style={{ position: 'relative', width: size, height: size, alignSelf: 'center' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#334155"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={gaugeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{ 
              transition: 'stroke-dashoffset 0.5s ease-in-out, stroke 0.3s ease',
              filter: 'drop-shadow(0 0 6px rgba(45, 212, 191, 0.15))'
            }}
          />
        </svg>
        
        {/* Center value */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: `${size * 0.15}px`,
            fontWeight: 'bold',
            color: INSTRUMENT.value,
            lineHeight: 1
          }}>
            {numValue.toFixed(decimals)}
          </div>
          {unit && (
            <div style={{
              fontSize: `${size * 0.08}px`,
              color: INSTRUMENT.muted,
              marginTop: '2px',
              fontWeight: '500'
            }}>
              {unit}
            </div>
          )}
        </div>
        
        {/* Min/Max labels */}
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          fontSize: `${size * 0.06}px`,
          color: INSTRUMENT.muted,
          fontWeight: '500'
        }}>
          {min}
        </div>
        <div style={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          fontSize: `${size * 0.06}px`,
          color: INSTRUMENT.muted,
          fontWeight: '500'
        }}>
          {max}
        </div>
      </div>
    </WidgetContainer>
  );
};

// Artless Gauge (minimalist style)
export const ArtlessGauge = ({ name, value, unit, min = 0, max = 100, config = {}, sourceType, isLive = false, lastUpdate = null }) => {
  const hasData = value !== '--' && value !== null && value !== undefined && value !== '';
  const parsedValue = parseFloat(value);
  const rawValue = isNaN(parsedValue) ? 0 : parsedValue;
  let numValue = rawValue;
  
  if (unit === '%' && numValue >= 0 && numValue <= 1) {
    numValue = numValue * 100;
  }
  
  const decimals = coerceWidgetDecimals(config, 1);
  const range = Math.max(max - min, 0.001); // Avoid division by zero
  const percentage = hasData ? ((numValue - min) / range) * 100 : 0;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  
  const size = config.size || 100;
  // Needle: arc goes left (0%) to right (100%); needle sweeps same direction
  const angle = (clampedPercentage / 100) * 180;
  
  return (
    <WidgetContainer>
      <WidgetTitleRow>
        <WidgetTitle>{name}</WidgetTitle>
        <DataSourceIndicator sourceType={sourceType || (config?.forceDemo || config?.dataMode === 'demo' ? 'demo' : (isLive ? 'live' : 'database'))} lastUpdate={lastUpdate} />
      </WidgetTitleRow>
      <div style={{ position: 'relative', width: '100%', maxWidth: size, aspectRatio: '1 / 0.6', margin: '0 auto' }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size * 0.6}`} preserveAspectRatio="xMidYMid meet">
          {/* Background arc */}
          <path
            d={`M 10 ${size * 0.5} A ${size * 0.4} ${size * 0.4} 0 0 1 ${size - 10} ${size * 0.5}`}
            fill="none"
            stroke="#334155"
            strokeWidth="3"
          />
          {/* Progress arc */}
          <path
            d={`M 10 ${size * 0.5} A ${size * 0.4} ${size * 0.4} 0 0 1 ${size - 10} ${size * 0.5}`}
            fill="none"
            stroke={INSTRUMENT.accent}
            strokeWidth="3"
            strokeDasharray={`${(clampedPercentage / 100) * 125} 125`}
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
          
          {/* Needle - arc is left(0%) to right(100%); line points right, so rotate(180-angle) sweeps left→right */}
          <g transform={`translate(${size / 2}, ${size * 0.5}) rotate(${180 - angle})`}>
            <line
              x1="0"
              y1="0"
              x2={size * 0.35}
              y2="0"
              stroke={INSTRUMENT.critical}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="0" cy="0" r="3" fill={INSTRUMENT.critical} />
          </g>
        </svg>
        
        {/* Value display */}
        <div style={{
          position: 'absolute',
          bottom: '5px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center'
        }}>
          {hasData ? (
            <>
              <WidgetValue style={{ fontSize: '1.1rem', margin: 0 }}>
                {numValue.toFixed(decimals)}
              </WidgetValue>
              {unit && <WidgetUnit style={{ fontSize: '0.75rem' }}>{unit}</WidgetUnit>}
            </>
          ) : (
            <WidgetValue style={{ fontSize: '1.1rem', margin: 0, color: INSTRUMENT.muted }}>--</WidgetValue>
          )}
        </div>
      </div>
    </WidgetContainer>
  );
};

// Linear Gauge (horizontal bar style — N2K instrument)
export const LinearGauge = ({ name, value, unit, min = 0, max = 100, config = {}, sourceType, isLive = false, lastUpdate = null }) => {
  const parsedValue = parseFloat(value);
  const rawValue = isNaN(parsedValue) ? 0 : parsedValue;
  let numValue = rawValue;
  
  if (unit === '%' && numValue >= 0 && numValue <= 1) {
    numValue = numValue * 100;
  }
  
  const decimals = coerceWidgetDecimals(config, 1);
  const range = Math.max(max - min, 0.001);
  const percentage = ((numValue - min) / range) * 100;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  const fillColor = instrumentBarColor(clampedPercentage, { name, config }, getColorForValue(clampedPercentage, config.colorRanges));

  return (
    <WidgetContainer>
      <WidgetTitleRow>
        <WidgetTitle>{name}</WidgetTitle>
        <DataSourceIndicator sourceType={sourceType || (config?.forceDemo || config?.dataMode === 'demo' ? 'demo' : (isLive ? 'live' : 'database'))} lastUpdate={lastUpdate} />
      </WidgetTitleRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline' }}>
        <WidgetValue style={{ fontSize: '1.35rem', margin: 0, color: fillColor }}>
          {numValue.toFixed(decimals)}{unit ? ` ${unit}` : ''}
        </WidgetValue>
      </div>
      <div style={{
        width: '100%',
        height: 14,
        backgroundColor: INSTRUMENT.track,
        borderRadius: 4,
        overflow: 'hidden',
        marginTop: '0.55rem',
        border: `1px solid ${INSTRUMENT.cardBorder}`,
      }}>
        <div style={{
          width: `${clampedPercentage}%`,
          height: '100%',
          background: fillColor,
          borderRadius: 3,
          transition: 'width 0.5s ease-in-out',
        }} />
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '0.35rem',
        fontSize: '0.7rem',
        color: INSTRUMENT.muted,
        fontWeight: '500'
      }}>
        <span>{min}</span>
        <span>{Math.round(clampedPercentage)}%</span>
        <span>{max}</span>
      </div>
    </WidgetContainer>
  );
};

// Tank Level Gauge — horizontal instrument bar (N2K-style)
export const TankGauge = ({ name, value, unit, min = 0, max = 1, config = {}, sourceType, isLive = false, lastUpdate = null }) => {
  const parsedValue = parseFloat(value);
  let numValue = isNaN(parsedValue) ? 0 : parsedValue;
  const decimals = coerceWidgetDecimals(config, 1);
  const isRatio = numValue >= 0 && numValue <= 1;
  const displayValue = isRatio ? numValue * 100 : numValue;
  const percentage = isRatio ? numValue * 100 : ((numValue - min) / (max - min)) * 100;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  const widgetHint = { name, data_path: config.dataPath, config };
  const fillColor = instrumentBarColor(
    clampedPercentage,
    widgetHint,
    getColorForValue(displayValue, config.colorRanges)
  );
  const capacity = parseFloat(config.capacity ?? config.capacityLiters ?? config.maxCapacity);
  const capacityUnit = config.capacityUnit || 'L';
  const capacityLine = Number.isFinite(capacity) && capacity > 0
    ? `${Math.round((clampedPercentage / 100) * capacity)} of ${Math.round(capacity)} ${capacityUnit}`
    : null;

  return (
    <WidgetContainer>
      <WidgetTitleRow>
        <WidgetTitle>{name}</WidgetTitle>
        <DataSourceIndicator sourceType={sourceType || (config?.forceDemo || config?.dataMode === 'demo' ? 'demo' : (isLive ? 'live' : 'database'))} lastUpdate={lastUpdate} />
      </WidgetTitleRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline' }}>
        <WidgetValue style={{ fontSize: '1.45rem', margin: 0, color: fillColor }}>
          {displayValue.toFixed(decimals)}%
        </WidgetValue>
      </div>
      <div style={{
        width: '100%',
        height: 16,
        backgroundColor: INSTRUMENT.track,
        borderRadius: 4,
        overflow: 'hidden',
        marginTop: '0.55rem',
        border: `1px solid ${INSTRUMENT.cardBorder}`,
      }}>
        <div style={{
          width: `${clampedPercentage}%`,
          height: '100%',
          backgroundColor: fillColor,
          transition: 'height 0.5s ease-in-out, width 0.5s ease-in-out, background-color 0.3s ease',
        }} />
      </div>
      {capacityLine && (
        <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: INSTRUMENT.muted, fontWeight: 500 }}>
          {capacityLine}
        </div>
      )}
      {!capacityLine && isWasteOrHoldingTank(widgetHint) && (
        <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: INSTRUMENT.muted, fontWeight: 500 }}>
          {clampedPercentage.toFixed(0)}% full
        </div>
      )}
    </WidgetContainer>
  );
};

// Speedometer Gauge
export const SpeedometerGauge = ({ name, value, unit, min = 0, max = 100, config = {}, sourceType, isLive = false, lastUpdate = null }) => {
  const hasData = value !== '--' && value !== null && value !== undefined && value !== '';
  const parsedValue = parseFloat(value);
  const rawValue = isNaN(parsedValue) ? 0 : parsedValue;
  let numValue = rawValue;
  
  if (unit === '%' && numValue >= 0 && numValue <= 1) {
    numValue = numValue * 100;
  }
  
  const decimals = coerceWidgetDecimals(config, 1);
  const range = Math.max(max - min, 0.001); // Avoid division by zero
  const percentage = hasData ? ((numValue - min) / range) * 100 : 0;
  const clampedPercentage = Math.max(0, Math.min(100, percentage));
  
  const size = config.size || 140;
  const angle = (clampedPercentage / 100) * 270 - 135; // 270 degree arc starting from bottom left
  
  return (
    <WidgetContainer>
      <WidgetTitleRow>
        <WidgetTitle>{name}</WidgetTitle>
        <DataSourceIndicator sourceType={sourceType || (config?.forceDemo || config?.dataMode === 'demo' ? 'demo' : (isLive ? 'live' : 'database'))} lastUpdate={lastUpdate} />
      </WidgetTitleRow>
      <div style={{ position: 'relative', width: '100%', maxWidth: size, aspectRatio: '1 / 0.8', margin: '0 auto' }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size * 0.8}`} preserveAspectRatio="xMidYMid meet">
          {/* Outer ring */}
          <circle
            cx={size / 2}
            cy={size * 0.7}
            r={size * 0.4}
            fill="none"
            stroke="#1f2937"
            strokeWidth="2"
          />
          
          {/* Tick marks */}
          {Array.from({ length: 11 }, (_, i) => {
            const tickAngle = (i * 27) - 135; // 270 degrees / 10 intervals
            const isMainTick = i % 2 === 0;
            const tickLength = isMainTick ? 15 : 8;
            const tickWidth = isMainTick ? 2 : 1;
            
            const x1 = size / 2 + Math.cos((tickAngle * Math.PI) / 180) * (size * 0.35);
            const y1 = size * 0.7 + Math.sin((tickAngle * Math.PI) / 180) * (size * 0.35);
            const x2 = size / 2 + Math.cos((tickAngle * Math.PI) / 180) * (size * 0.35 - tickLength);
            const y2 = size * 0.7 + Math.sin((tickAngle * Math.PI) / 180) * (size * 0.35 - tickLength);
            
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#374151"
                strokeWidth={tickWidth}
              />
            );
          })}
          
          {/* Needle */}
          <g transform={`translate(${size / 2}, ${size * 0.7})`}>
            <line
              x1="0"
              y1="0"
              x2={Math.cos((angle * Math.PI) / 180) * (size * 0.3)}
              y2={Math.sin((angle * Math.PI) / 180) * (size * 0.3)}
              stroke="#dc2626"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle cx="0" cy="0" r="6" fill="#dc2626" />
            <circle cx="0" cy="0" r="3" fill="#fca5a5" />
          </g>
        </svg>
        
        {/* Digital readout */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1f2937',
          color: hasData ? '#10b981' : '#6b7280',
          padding: '4px 8px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.9rem',
          fontWeight: 'bold'
        }}>
          {hasData ? `${numValue.toFixed(decimals)} ${unit || ''}`.trim() : '--'}
        </div>
      </div>
    </WidgetContainer>
  );
};